import { formatPersonName } from '@/utils/personName';
/**
 * IndividualPersonDialog — Personal-group entry point for the RAO people form.
 *
 * v0.14.48 (Bill 2026-05-26, BACKLOG-0070) — reduced to a thin wrapper around
 * `IndividualReviewBody`. The section tree + tab state + draft management now
 * live in the body component so PdwShell can mount the same Stage 9 surface
 * inline (one persistent modal end-to-end per `feedback_pdw_breadcrumb_
 * disjointed_must_match_spec_20260513`). This wrapper preserves the legacy
 * ModalShell + PdwFooterDyad surface for callers that still come in via the
 * PersonDialog dispatcher:
 *   - RAO PeopleStep — review-add (existing CIF hit) + edit flows (the new
 *     "+ New Person" path now routes through PdwShell directly)
 *   - RCM Maint CIF — "Edit CIF" button (mode='edit')
 *   - RCM JointOwnerAddDialog — joint-owner add path
 *
 * Save dispatch + the Return/Continue dyad still live here for the wrapper
 * usage; the new shell owns its own footer when the body is mounted inline.
 */

import { useCallback, useEffect, useState } from 'react';
import { ModalShell } from '@/components/primitives';
import type { RaoPerson } from '../../../types';
import { PdwFooterDyad } from '../shared/PdwFooterDyad';
import {
  IndividualReviewBody,
  type IndividualReviewBodyMeta,
} from './IndividualReviewBody';

export type IndividualPersonDialogMode = 'review-add' | 'new' | 'edit';

export interface IndividualPersonDialogProps {
  open: boolean;
  mode: IndividualPersonDialogMode;
  person: RaoPerson;
  onSave: (person: RaoPerson) => void;
  onClose: () => void;
  cartCifIds?: ReadonlyArray<string>;
  /** Required by DocumentSlotsSection for per-cart document attachment. */
  cartId: string;
  /** Required by DocumentSlotsSection for upload metadata. Defaults to 'UNKNOWN' if AuthContext isn't wired yet. */
  officerCode?: string;
  /** When true, disables every input + hides edit/save controls. Passed to all sections. */
  readOnly?: boolean;
  /** When false, hides the CIP scan panel (used in Maint CIF edit). Defaults true. */
  cipScanEnabled?: boolean;
}

export function IndividualPersonDialog({
  open,
  mode,
  person,
  onSave,
  onClose,
  cartCifIds,
  cartId,
  officerCode,
  readOnly = false,
  cipScanEnabled = true,
}: IndividualPersonDialogProps): JSX.Element | null {
  // Latest draft + metadata mirrored up from IndividualReviewBody.
  const [draft, setDraft] = useState<RaoPerson>(person);
  const [meta, setMeta] = useState<IndividualReviewBodyMeta>({
    hasChanges: false,
    dupInCart: false,
    saveDisabled: false,
    activeTab: 'review',
  });

  useEffect(() => {
    setDraft(person);
  }, [person]);

  const handleDraftChange = useCallback(
    (next: RaoPerson, nextMeta: IndividualReviewBodyMeta): void => {
      setDraft(next);
      setMeta(nextMeta);
    },
    [],
  );

  const displayName =
    draft.firstName || draft.lastName
      ? formatPersonName(draft)
      : draft.name2 ?? '';

  const title =
    mode === 'review-add'
      ? `Review & add — ${displayName}`
      : mode === 'new'
        ? 'Add new person'
        : `Edit person — ${displayName}`;

  const btnLabel =
    mode === 'review-add'
      ? meta.hasChanges ? 'Add existing w/ changes' : 'Add existing'
      : mode === 'new'
        ? 'Add new'
        : 'Save changes';

  // Wrapper-mode footer: Continue ALWAYS saves (label flips to mode-aware
  // save label via PdwFooterDyad's lastStepLabel). Tab navigation is via
  // the in-body PdwTabStrip click; Return is disabled because the wrapper
  // is a leaf surface (the new shell handles inter-stage nav). The prior
  // tab-advance-on-Continue behaviour moved into PdwShell with v0.14.48.
  const onReturn = useCallback((): void => {
    // Intentionally empty — Return disabled in wrapper mode.
  }, []);

  const onContinue = useCallback((): void => {
    if (!meta.saveDisabled && !readOnly) onSave(draft);
  }, [draft, meta.saveDisabled, readOnly, onSave]);

  const continueDisabled = meta.saveDisabled || readOnly;

  return (
    <ModalShell open={open} onClose={onClose} title={title} size="lg">
      <IndividualReviewBody
        person={person}
        mode={mode}
        cartId={cartId}
        {...(officerCode !== undefined ? { officerCode } : {})}
        {...(cartCifIds !== undefined ? { cartCifIds } : {})}
        readOnly={readOnly}
        cipScanEnabled={cipScanEnabled}
        onDraftChange={handleDraftChange}
      />

      <PdwFooterDyad
        currentStep={0}
        totalSteps={1}
        onReturn={onReturn}
        onContinue={onContinue}
        returnDisabled
        continueDisabled={continueDisabled}
        {...(meta.saveDisabledReason ? { continueDisabledReason: meta.saveDisabledReason } : {})}
        lastStepLabel={`${btnLabel} →`}
      />
    </ModalShell>
  );
}
